import React, { useMemo } from 'react';
import { sanitizeHtml } from '../../utils/textProcessing.js';
import SourcesCitedBlock from './SourcesCitedBlock.jsx';

// Landing brand-color: mirrors modes.js → { id: 'landing', color: '#10A37F' }.
const LANDING_COLOR = '#10A37F';

// QA bar color thresholds — mirrors InstagramResultEntry / NewsletterResultEntry / SeoResultEntry.
const qaColor = (score) =>
  score >= 70 ? '#10B981' : score >= 50 ? '#F59E0B' : '#EF4444';

const splitParagraphs = (text) =>
  String(text || '')
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean);

const SectionLabel = ({ children, color, t }) => (
  <div
    style={{
      fontSize: '10px',
      fontFamily: "'JetBrains Mono', monospace",
      color: color || t.textMuted || '#7a7a8e',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      fontWeight: 600,
      marginBottom: '8px',
    }}
  >
    {children}
  </div>
);

// Renders a paragraph body with inline HTML allowed (problem.content / solution.content / features.items[].content).
// BE does NOT pre-sanitize — sanitization happens here.
const ParagraphBody = ({ text }) => {
  const paras = useMemo(() => splitParagraphs(text), [text]);
  if (paras.length === 0) return null;
  return (
    <>
      {paras.map((p, i) => (
        <p
          key={i}
          style={{
            margin: i === paras.length - 1 ? 0 : '0 0 10px',
            fontSize: '14px',
            lineHeight: 1.7,
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(p) }}
        />
      ))}
    </>
  );
};

const CtaAnchor = ({ url, label, variant, color }) => {
  // variant: 'hero' = filled-light; 'final' = filled-solid.
  const isFinal = variant === 'final';
  const trimmed = (url || '').trim();
  const baseStyle = isFinal
    ? {
        display: 'inline-block',
        padding: '10px 24px',
        background: color,
        borderRadius: '8px',
        fontWeight: 600,
        color: '#fff',
        fontSize: '14px',
        textDecoration: 'none',
      }
    : {
        display: 'inline-block',
        padding: '8px 20px',
        background: `${color}20`,
        border: `1px solid ${color}50`,
        borderRadius: '6px',
        fontWeight: 600,
        color,
        fontSize: '14px',
        textDecoration: 'none',
      };
  const text = (label || '').trim() || 'Scopri di più';
  if (!trimmed) {
    return (
      <span
        aria-disabled="true"
        style={{ ...baseStyle, opacity: 0.55, cursor: 'not-allowed' }}
        title="Nessun URL CTA — imposta brief.cta_url"
      >
        {text}
      </span>
    );
  }
  return (
    <a href={trimmed} target="_blank" rel="noopener noreferrer" style={baseStyle}>
      {text}
    </a>
  );
};

// NOTE: copiedId and onCopy ARE used — they are passed to the embedded
// SourcesCitedBlock which has built-in "Copia HTML" / "Copia Testo" buttons.
// Do NOT mark them as unused.
const LandingResultEntry = ({
  entry,
  copiedId,
  onCopy,
  w = (o) => `rgba(255,255,255,${o})`,
  t = {},
}) => {
  const r = entry.results || {};
  const c = useMemo(() => r.content || {}, [r.content]);
  const color = entry.modeConfig?.color || LANDING_COLOR;
  const eId = entry.id || 'landing';

  const qaScore = r.metadata?.qa_score ?? r.metadata?.style_compliance ?? null;
  const ctaUrl = (c.cta_url || '').trim();

  const hero = c.hero || {};
  const problem = c.problem || {};
  const solution = c.solution || {};
  const features = c.features || {};
  const featureItems = Array.isArray(features.items) ? features.items : [];
  const socialProof = c.social_proof || {};
  const spBullets = Array.isArray(socialProof.bullets) ? socialProof.bullets : [];
  const faqItems = Array.isArray(c.faq) ? c.faq : [];
  const finalCta = c.final_cta || {};

  const sectionStyle = {
    marginBottom: '14px',
    padding: '14px 16px',
    background: w(0.02),
    borderRadius: '10px',
    border: `1px solid ${w(0.06)}`,
  };

  return (
    <div>
      {/* Region 1 — QA Score bar */}
      {qaScore != null && (
        <div style={{ marginBottom: '18px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '6px',
            }}
          >
            <SectionLabel t={t}>QA Score</SectionLabel>
            <span
              style={{
                fontSize: '13px',
                fontWeight: 700,
                color: qaColor(qaScore),
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              {qaScore}/100
            </span>
          </div>
          <div
            style={{
              height: '6px',
              borderRadius: '4px',
              background: w(0.06),
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${Math.min(100, Math.max(0, qaScore))}%`,
                height: '100%',
                background: qaColor(qaScore),
                transition: 'width 0.4s ease',
              }}
            />
          </div>
        </div>
      )}

      {/* Region 2 — HERO */}
      <section id="hero" style={sectionStyle}>
        <SectionLabel color={color} t={t}>HERO</SectionLabel>
        <div style={{ fontWeight: 700, fontSize: '20px', lineHeight: 1.3, marginBottom: '6px', color: t.text }}>
          {hero.headline || ''}
        </div>
        {hero.subheadline && (
          <div style={{ fontSize: '14px', color: t.textMuted || '#7a7a8e', marginBottom: '12px', lineHeight: 1.55 }}>
            {hero.subheadline}
          </div>
        )}
        <CtaAnchor url={ctaUrl || hero.cta_url} label={hero.cta_text} variant="hero" color={color} />
      </section>

      {/* Region 3 — PROBLEM */}
      <section id="problema" style={sectionStyle}>
        <SectionLabel t={t}>PROBLEMA</SectionLabel>
        {problem.heading && (
          <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '8px', color: t.text }}>
            {problem.heading}
          </div>
        )}
        <ParagraphBody text={problem.content} />
      </section>

      {/* Region 4 — SOLUTION */}
      <section id="soluzione" style={sectionStyle}>
        <SectionLabel t={t}>SOLUZIONE</SectionLabel>
        {solution.heading && (
          <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '8px', color: t.text }}>
            {solution.heading}
          </div>
        )}
        <ParagraphBody text={solution.content} />
      </section>

      {/* Region 5 — FEATURES (hide entirely when items empty) */}
      {featureItems.length > 0 && (
        <section id="caratteristiche" style={sectionStyle}>
          <SectionLabel t={t}>{`FEATURES (${featureItems.length})`}</SectionLabel>
          {featureItems.map((item, i) => (
            <div
              key={i}
              style={{
                padding: '10px 12px',
                background: w(0.03),
                borderRadius: '8px',
                marginBottom: i === featureItems.length - 1 ? 0 : '8px',
              }}
            >
              {item.heading && (
                <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px', color: t.text }}>
                  {item.heading}
                </div>
              )}
              <ParagraphBody text={item.content} />
            </div>
          ))}
        </section>
      )}

      {/* Region 6 — SOCIAL_PROOF (hide entirely when bullets empty) */}
      {spBullets.length > 0 && (
        <section id="social-proof" style={sectionStyle}>
          <SectionLabel color={color} t={t}>SOCIAL PROOF</SectionLabel>
          {socialProof.heading && (
            <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: '8px', color: t.text }}>
              {socialProof.heading}
            </div>
          )}
          <ul style={{ paddingLeft: '20px', margin: 0 }}>
            {spBullets.map((bullet, i) => (
              <li
                key={i}
                style={{
                  fontSize: '13.5px',
                  lineHeight: 1.6,
                  marginBottom: i === spBullets.length - 1 ? 0 : '6px',
                  color: t.text,
                }}
              >
                {bullet}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Region 7 — FAQ (hide entirely when empty) */}
      {faqItems.length > 0 && (
        <section id="faq" style={sectionStyle}>
          <SectionLabel t={t}>{`FAQ (${faqItems.length})`}</SectionLabel>
          {faqItems.map((item, i) => (
            <div
              key={i}
              style={{
                marginBottom: i === faqItems.length - 1 ? 0 : '10px',
                paddingBottom: i === faqItems.length - 1 ? 0 : '10px',
                borderBottom: i === faqItems.length - 1 ? 'none' : `1px solid ${w(0.05)}`,
              }}
            >
              <div style={{ fontWeight: 600, fontSize: '13.5px', marginBottom: '4px', color: t.text }}>
                {item.question || ''}
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: '13px',
                  lineHeight: 1.6,
                  color: t.textMuted || '#7a7a8e',
                }}
              >
                {item.answer || ''}
              </p>
            </div>
          ))}
        </section>
      )}

      {/* Region 8 — FINAL_CTA */}
      <section id="cta-finale" style={sectionStyle}>
        <SectionLabel color={color} t={t}>FINAL CTA</SectionLabel>
        {finalCta.tagline && (
          <div style={{ fontWeight: 700, fontSize: '17px', lineHeight: 1.35, marginBottom: '12px', color: t.text }}>
            {finalCta.tagline}
          </div>
        )}
        <CtaAnchor url={ctaUrl || finalCta.cta_url} label={finalCta.cta_text} variant="final" color={color} />
      </section>

      {/* Region 9 — Sources cited (shared variant-agnostic block; consumes copiedId + onCopy) */}
      <SourcesCitedBlock
        sources={r.sources?.cited}
        copiedId={copiedId}
        onCopy={onCopy}
        entryId={eId}
        w={w}
        t={t}
      />
    </div>
  );
};

export default LandingResultEntry;
